/**
 * The CFC Product Analytics action taxonomy. Spec: Confluence ENT/3699081221.
 *
 * Names are `<area>-<object>-<verb>`, where `<area>` is a stable functional module rather than a
 * visual position. They are fixed here so the same button is tracked under one name in every
 * language, instead of the DE/EN split that auto-capture produces.
 *
 * Facet values (the `context` argument of `monitor.action`) appear to need no Datadog-side
 * setup; `docs/rum-custom-facets.md` has the evidence. They do need a closed union here, because
 * `monitor.action` types the payload as `Record<string, unknown>` and checks nothing.
 */
export const rumActionNames = {
  ccfOpened: "cfc-ccf-opened",
  pcfOpened: "cfc-pcf-opened",
  breadcrumbNavigated: "cfc-breadcrumb-navigated",

  dataEntrySingleOpened: "cfc-data-entry-single-opened",
  dataEntrySaved: "cfc-data-entry-saved",
  dataEntryExported: "cfc-data-entry-exported",
  dataEntryUpdated: "cfc-data-entry-updated",
  dataEntryUpdateError: "cfc-data-entry-update-error",
  dataEntryBulkUpdated: "cfc-data-entry-bulk-updated",
  dataEntryBulkUpdateError: "cfc-data-entry-bulk-update-error",
  dataEntryDeleted: "cfc-data-entry-deleted",
  dataEntryEditOpened: "cfc-data-entry-edit-opened",
  dataEntryChangesDiscarded: "cfc-data-entry-changes-discarded",
  dataEntrySupportingInformationToggled: "cfc-data-entry-supporting-information-toggled",

  massUploadOpened: "cfc-mass-upload-opened",
  massUploadCompleted: "cfc-mass-upload-completed",
  massUploadError: "cfc-mass-upload-error",
  massUploadUpdated: "cfc-mass-upload-updated",
  massUploadUpdateError: "cfc-mass-upload-update-error",
  massUploadCancelled: "cfc-mass-upload-cancelled",
  massUploadDiscarded: "cfc-mass-upload-discarded",

  calculationCreated: "cfc-calculation-created",
  calculationStateChanged: "cfc-calculation-state-changed",
  calculationUpdated: "cfc-calculation-updated",
  calculationCloned: "cfc-calculation-cloned",
  calculationDeleted: "cfc-calculation-deleted",
  calculationsArchived: "cfc-calculations-archived",
  customerGroupingCreated: "cfc-customer-grouping-created",

  curatedListsExportRequested: "cfc-curated-lists-export-requested",
  curatedListsExported: "cfc-curated-lists-exported",
  curatedListsExportError: "cfc-curated-lists-export-error",

  emissionSourceOpened: "cfc-emission-source-opened",
  emissionSourceExcluded: "cfc-emission-source-excluded",
  emissionSourceIncluded: "cfc-emission-source-included",
  emissionSourceCompletionChanged: "cfc-emission-source-completion-changed",

  supportRequested: "cfc-support-requested",
  collaborationRequested: "cfc-collaboration-requested",
  surveyInvitationSent: "cfc-survey-invitation-sent",
  surveyInvitationDeleteConfirmed: "cfc-survey-invitation-delete-confirmed",
  surveyInvitationDeleted: "cfc-survey-invitation-deleted",
  surveyInvitationDeleteError: "cfc-survey-invitation-delete-error",

  customEmissionFactorCreated: "cfc-custom-emission-factor-created",
  customEmissionFactorDeleted: "cfc-custom-emission-factor-deleted",
  customEmissionFactorUpdated: "cfc-custom-emission-factor-updated",
  customEmissionFactorEditOpened: "cfc-custom-emission-factor-edit-opened",
  customEmissionFactorViewOpened: "cfc-custom-emission-factor-view-opened",
  customEmissionFactorDeleteOpened: "cfc-custom-emission-factor-delete-opened",
  customEmissionFactorBulkDeleteOpened: "cfc-custom-emission-factor-bulk-delete-opened",
  customEmissionFactorUploadOpened: "cfc-custom-emission-factor-upload-opened",
  customEmissionFactorMethodologyAndBreakdownExpandAll:
    "cfc-custom-emission-factor-methodology-and-breakdown-expand-all",
  customEmissionFactorMethodologyAndBreakdownCollapseAll:
    "cfc-custom-emission-factor-methodology-and-breakdown-collapse-all",
  customEmissionFactorFormNarrowed: "cfc-custom-emission-factor-form-narrowed",
  customEmissionFactorFormWidened: "cfc-custom-emission-factor-form-widened",

  documentUploaded: "cfc-document-uploaded",
  documentDeleted: "cfc-document-deleted",

  documentUploadOk: "document.upload.ok",
  documentUploadError: "document.upload.error",
  documentUploadInterrupted: "document.upload.interrupted",
  documentDownloadOk: "document.download.ok",
  documentDownloadError: "document.download.error",
  documentDeleteOk: "document.delete.ok",
  documentDeleteError: "document.delete.error",
  documentPatchOk: "document.patch.ok",
  documentPatchError: "document.patch.error",
  documentTableSearchUsed: "document.table.search.used",
  documentTableFilterUsed: "document.table.filter.used",
  documentTableExportUsed: "document.table.export.used",

  // Moved from communication-dashboard with their names, so its dashboards keep counting them.
  communicationTransparencyPageOpened: "homepage_transparency_ctp_url_redirected",
  communicationTransparencyEmptyStateOpened: "homepage_transparency_empty_state_redirected",
  communicationTransparencyEmptyStateViewed: "transparency_page_empty_state_viewed",
  communicationReportDownloaded: "report_downloaded",
  communicationSampleReportDownloaded: "sample_report_downloaded",
  communicationReportDownloadError: "cfc-communication-report-download-error",
  communicationSampleReportDownloadError: "cfc-communication-sample-report-download-error",

  // Formerly `cfc-emission-source-task*`; the threads dashboard queries both names to keep the history.
  collaborationTasksOpened: "cfc-collaboration-tasks-opened",
  collaborationTaskCreateOpened: "cfc-collaboration-task-create-opened",
  collaborationTaskCreateSubmitted: "cfc-collaboration-task-create-submitted",
  collaborationTaskReplyOpened: "cfc-collaboration-task-reply-opened",
  collaborationTaskReplySubmitted: "cfc-collaboration-task-reply-submitted",
  collaborationTaskResolveClicked: "cfc-collaboration-task-resolve-clicked",
  collaborationTaskReopenClicked: "cfc-collaboration-task-reopen-clicked",
  collaborationTaskResolvedListToggled: "cfc-collaboration-task-resolved-list-toggled",
  collaborationTaskCommentPosted: "cfc-collaboration-task-comment-posted",
  collaborationTaskCommentPostError: "cfc-collaboration-task-comment-post-error",
  collaborationTaskStatusChanged: "cfc-collaboration-task-status-changed",
  collaborationTaskStatusChangeError: "cfc-collaboration-task-status-change-error",
  collaborationTaskCommentEditClicked: "cfc-collaboration-task-comment-edit-clicked",
  collaborationTaskCommentEditSubmitted: "cfc-collaboration-task-comment-edit-submitted",
  collaborationTaskCommentEdited: "cfc-collaboration-task-comment-edited",
  collaborationTaskCommentEditError: "cfc-collaboration-task-comment-edit-error",
  collaborationTaskCommentDeleteClicked: "cfc-collaboration-task-comment-delete-clicked",
  collaborationTaskCommentDeleteConfirmed: "cfc-collaboration-task-comment-delete-confirmed",
  collaborationTaskCommentDeleted: "cfc-collaboration-task-comment-deleted",
  collaborationTaskCommentDeleteError: "cfc-collaboration-task-comment-delete-error",
  collaborationNotificationOpened: "cfc-collaboration-notification-opened",
  collaborationThreadsListSelected: "cfc-collaboration-threads-list-selected",
  collaborationThreadsDoneToggled: "cfc-collaboration-threads-done-toggled",
  collaborationThreadsExpandToggled: "cfc-collaboration-threads-expand-toggled",
  collaborationThreadsThreadOpened: "cfc-collaboration-threads-thread-opened",

  pcfCalculationCreated: "cfc-pcf-calculation-created",
  pcfDataCollectionFinished: "cfc-pcf-data-collection-finished",
  pcfValidationRequested: "cfc-pcf-validation-requested",
  pcfValidationPurchased: "cfc-pcf-validation-purchased",
  pcfCalculationReopened: "cfc-pcf-calculation-reopened",
  pcfCalculationArchived: "cfc-pcf-calculation-archived",
  pcfExported: "cfc-pcf-exported",
  pcfExportError: "cfc-pcf-export-error",
  pcfTemplateUploaded: "cfc-pcf-template-uploaded",
  pcfTemplateUploadError: "cfc-pcf-template-upload-error",
  pcfTemplateValidated: "cfc-pcf-template-validated",
  pcfTemplateErrorsResolved: "cfc-pcf-template-errors-resolved",
  pcfTemplateErrorsResolveError: "cfc-pcf-template-errors-resolve-error",
  pcfUsePhaseOtherAddOpened: "cfc-pcf-use-phase-other-add-opened",
  pcfReportImportOpened: "cfc-pcf-report-import-opened",
  pcfReportImportUploaded: "cfc-pcf-report-import-uploaded",
  pcfReportImportDraftFinalized: "cfc-pcf-report-import-draft-finalized",
  pcfReportImportDraftDiscarded: "cfc-pcf-report-import-draft-discarded",
  pcfReportImportArchived: "cfc-pcf-report-import-archived",
  pcfCreditCardInterest: "cfc-pcf-credit-card-interest",
  pcfWasteCategoryConfirmed: "cfc-pcf-waste-category-confirmed",

  networkSupportPackageOpened: "cfc-network-support-package-opened",

  networkInvitationLinkCopied: "cfc-network-invitation-link-copied",

  networkBuyerPreviewToggled: "cfc-network-buyer-preview-toggled",

  // The select-account page's retry after a failed authenticated resolve (anything but a 404).
  networkSupplierInviteResolveRetried: "cfc-network-supplier-invite-resolve-retried",

  // The supplier bulk upload funnel. `source` tells files the supplier agent prepared from manual
  // ones, and `trace_id` joins an agent file back to the prompt copy that started it.
  networkSupplierAgentPromptCopied: "cfc-network-supplier-agent-prompt-copied",
  networkSupplierAgentPromptCopyError: "cfc-network-supplier-agent-prompt-copy-error",
  networkSupplierAgentPromptFeedback: "cfc-network-supplier-agent-prompt-feedback",
  networkSupplierAgentPromptFeedbackReason: "cfc-network-supplier-agent-prompt-feedback-reason",
  networkBulkUploadFileUploaded: "cfc-network-bulk-upload-file-uploaded",
  networkBulkUploadFileUploadError: "cfc-network-bulk-upload-file-upload-error",
  networkBulkUploadRowEdited: "cfc-network-bulk-upload-row-edited",
  networkBulkUploadRowDeleted: "cfc-network-bulk-upload-row-deleted",
  networkBulkUploadRequestsSubmitted: "cfc-network-bulk-upload-requests-submitted",
  networkBulkUploadRequestsSubmitError: "cfc-network-bulk-upload-requests-submit-error",

  // One name for every knowledge card: the `item`/`kind` facets carry which one, so a name per
  // card would only duplicate the facet. Same for `homepageSolutionOpened` below.
  networkKnowledgeOpened: "cfc-network-knowledge-opened",

  homescreenSolutionGuideDownloaded: "cfc-homescreen-solution-guide-downloaded",

  // The three homepage solution cards share one name; the `solution` facet records which one.
  homepageSolutionOpened: "cfc-homepage-solution-opened",
  homepageAnalyticsOpened: "cfc-homepage-analytics-opened",

  // These keep the `rum-` prefix the convention drops: a dashboard widget counts them (see
  // `useNetworkRoleUserTracking`), so renaming would silently empty it.
  networkBuyerArea: "rum-network-buyer-area",
  networkSupplierArea: "rum-network-supplier-area",

  reductionLandingViewed: "cfc-reduction-landing-viewed",
  reductionPlanStarted: "cfc-reduction-plan-started",
  reductionPcfPlanStarted: "cfc-reduction-pcf-plan-started",
  reductionPlanResumed: "cfc-reduction-plan-resumed",
  reductionPlanCreated: "cfc-reduction-plan-created",
  reductionScenariosOpened: "cfc-reduction-scenarios-opened",
  reductionPlanSubmitted: "cfc-reduction-plan-submitted",
  reductionMeasureCreated: "cfc-reduction-measure-created",
  reductionMeasureDuplicateOpened: "cfc-reduction-measure-duplicate-opened",
  reductionMeasureRemoveConfirmed: "cfc-reduction-measure-remove-confirmed",
  reductionMeasureRemoved: "cfc-reduction-measure-removed",
  reductionMeasureRemoveError: "cfc-reduction-measure-remove-error",
  reductionMeasuresClearConfirmed: "cfc-reduction-measures-clear-confirmed",
  reductionMeasuresCleared: "cfc-reduction-measures-cleared",
  reductionMeasuresClearError: "cfc-reduction-measures-clear-error",
  reductionPlanApproved: "cfc-reduction-plan-approved",
  reductionPlanRejected: "cfc-reduction-plan-rejected",
  reductionRoadmapViewed: "cfc-reduction-roadmap-viewed",
  reductionBaselineCalculationOpened: "cfc-reduction-baseline-calculation-opened",
  reductionBaselineSelectionCleared: "cfc-reduction-baseline-selection-cleared",
  reductionBaselineFiltersToggled: "cfc-reduction-baseline-filters-toggled",
  reductionBaselineFilterApplied: "cfc-reduction-baseline-filter-applied",
  reductionGuideDownloaded: "cfc-reduction-guide-downloaded",
  reductionRenewableEnergyQuoteRequested: "cfc-reduction-renewable-energy-quote-requested",
  reductionExpertConsultationStarted: "cfc-reduction-expert-consultation-started",
  reductionExpertConsultationRequested: "cfc-reduction-expert-consultation-requested",
  reductionFullAccessRequestStarted: "cfc-reduction-full-access-request-started",
  reductionFullAccessRequestSubmitted: "cfc-reduction-full-access-request-submitted",
  reductionScenarioSuggestionAdded: "cfc-reduction-scenario-suggestion-added",
  reductionReportDialogOpened: "cfc-reduction-report-dialog-opened",
  reductionReportRequested: "cfc-reduction-report-requested",
  reductionReportGenerateConfirmed: "cfc-reduction-report-generate-confirmed",
  reductionReportGenerated: "cfc-reduction-report-generated",
  reductionReportGenerateError: "cfc-reduction-report-generate-error",
  reductionPlansFiltered: "cfc-reduction-plans-filtered",
  reductionForecastGrowthToggled: "cfc-reduction-forecast-growth-toggled",
  reductionForecastFiltersCleared: "cfc-reduction-forecast-filters-cleared",
  reductionForecastFilterOverlayClosed: "cfc-reduction-forecast-filter-overlay-closed",
  reductionMeasureFiltersCleared: "cfc-reduction-measure-filters-cleared",
  reductionMeasureDataEntryOpened: "cfc-reduction-measure-data-entry-opened",
  reductionMeasureFilterOverlayClosed: "cfc-reduction-measure-filter-overlay-closed",
  reductionMeasureFilterReset: "cfc-reduction-measure-filter-reset",
  reductionMeasureStageFilterToggled: "cfc-reduction-measure-stage-filter-toggled",
  reductionAcademyOpened: "cfc-reduction-academy-opened",
  reductionKnowledgeOpened: "cfc-reduction-knowledge-opened",
  reductionBaselineChangedReviewed: "cfc-reduction-baseline-changed-reviewed",
  reductionBaselineChangedDismissed: "cfc-reduction-baseline-changed-dismissed",
} as const;

/**
 * Every name the app may track. `useMonitor().action` takes this rather than `string`, so an
 * ad-hoc or computed name is a compile error instead of a fragment of a second taxonomy.
 */
export type RumActionName = (typeof rumActionNames)[keyof typeof rumActionNames];

export type DocumentErrorType = "s3" | "api_4xx" | "api_5xx" | "network" | "unknown";

export type DocumentDownloadMode = "download" | "preview";

export type DocumentFileSizeBucket = "<1MB" | "1-10MB" | "10-50MB" | "50-100MB" | ">100MB";

/** Entry method tracked on {@link rumActionNames.dataEntrySaved}. */
export type EntryMethod = "single" | "mass";

/**
 * The `@context.template` facet of the mass-upload actions: the activity-data template, or the
 * "consumption with EF" template that takes an emission factor per row.
 */
export type MassUploadTemplate = "activity" | "emission_factor" | "ef_library";

/** The `@context.section` facet of {@link rumActionNames.pcfTemplateErrorsResolved}. */
export type PcfTemplateSection = "materials" | "electricity" | "combustion" | "product_properties";

/**
 * The `@context.scenario` facet of the PCF report import actions: where the wizard was opened, and so
 * what finishing it creates (a PCF, an emission factor, or a PCF for a buyer).
 */
export type PcfReportImportScenario = "pcfLibrary" | "efLibrary" | "network";

/** The `@context.kind` facet of {@link rumActionNames.collaborationTaskCommentPosted} and its error sibling. */
export type CollaborationTaskCommentKind = "create" | "reply";

/** The `@context.status` facet of {@link rumActionNames.collaborationTaskStatusChanged} and its error sibling. */
export type CollaborationTaskStatus = "resolved" | "open";

/** The `@context.targetType` facet of the task write actions: what the task is on. */
export type TaskTargetFacet = "calculation" | "emission_source" | "data_entry";

/** The `@context.channel` facet of {@link rumActionNames.collaborationNotificationOpened}: where the link was clicked. */
export type NotificationChannelFacet = "in_app" | "email";

/** The `@context.state` facet of {@link rumActionNames.surveyInvitationDeleted} and its error sibling. */
export type SurveyInvitationDeletedState = "draft" | "error";

/**
 * The `@context.item` facet of {@link rumActionNames.networkKnowledgeOpened}. Closed here rather
 * than derived from the card arrays so that retitling or re-linking a card cannot silently start a
 * second facet value and split its own history.
 */
export type NetworkKnowledgeItem =
  | "supplier-engagement-scope-3"
  | "secondary-to-primary-data-guide"
  | "scope-31-primary-data"
  | "network-pcf-video"
  | "pcf-checklist"
  | "anker-case-study"
  | "decarbonise-supply-chain-recording"
  | "calculate-with-climatepartner"
  | "pre-invitation-email-template"
  | "bulk-supplier-product-request-template";

/** Card type tracked alongside {@link NetworkKnowledgeItem} as the `@context.kind` facet. */
export type NetworkKnowledgeKind = "article" | "video" | "template";

/**
 * The `@context.source` facet of the bulk upload actions: `agent` only when the file carries a
 * valid supplier-agent marker, `manual` otherwise.
 */
export type BulkUploadSource = "agent" | "manual";

/**
 * The `@context.agent` facet of the bulk upload actions for an agent file of skill version 3 or
 * later: the agent as it reported itself, `other` when it named one off this list, `unknown` when it
 * could not tell its product or left the placeholder.
 */
export const SUPPLIER_AGENTS = [
  "claude-code",
  "claude",
  "chatgpt",
  "copilot",
  "cursor",
  "gemini",
  "grok",
  "other",
  "unknown",
] as const;

export type SupplierAgent = (typeof SUPPLIER_AGENTS)[number];

/** The `@context.placement` facet of {@link rumActionNames.networkSupplierAgentPromptCopied}: which agent prompt field was copied. */
export type SupplierAgentPromptPlacement = "home" | "uploader";

/**
 * The `@context.reason` facet of {@link rumActionNames.networkSupplierAgentPromptCopyError}. `unavailable`
 * is a browser without a clipboard, `refused` a clipboard write that was rejected.
 */
export type SupplierAgentPromptCopyErrorReason = "unavailable" | "refused";

/**
 * The `@context.helpful` facet of {@link rumActionNames.networkSupplierAgentPromptFeedback}. `dismissed`
 * is the feedback closed without an answer.
 */
export type SupplierAgentPromptFeedbackAnswer = "yes" | "no" | "dismissed";

/** What the buyer can pick as the thing that got in the way of the agent prompt, in the order they are listed. */
export const SUPPLIER_AGENT_PROMPT_FEEDBACK_REASONS = [
  "wrong_format",
  "too_many_steps",
  "prefer_direct_connection",
  "security_privacy",
  "other",
] as const;

/**
 * The `@context.reason` facet of {@link rumActionNames.networkSupplierAgentPromptFeedbackReason}. `dismissed`
 * is the follow-up closed without a pick.
 */
export type SupplierAgentPromptFeedbackReason = (typeof SUPPLIER_AGENT_PROMPT_FEEDBACK_REASONS)[number] | "dismissed";

/**
 * The `@context.error` facet of {@link rumActionNames.networkBulkUploadFileUploadError}: why the file could not be parsed.
 * `unknown_supplier_id` and `ambiguous_supplier_name` are a Products row that cannot be tied to one supplier.
 */
export type BulkUploadParseError = "empty" | "corrupt" | "unknown_supplier_id" | "ambiguous_supplier_name";

/** The `@context.field` facet of {@link rumActionNames.networkBulkUploadRowEdited}. */
export type BulkUploadEditedField =
  "supplierName" | "supplierId" | "contactName" | "email" | "location" | "productName" | "productId";

/** The `@context.target` facet of {@link rumActionNames.networkBulkUploadRowDeleted}. */
export type BulkUploadDeletedRow = "supplier" | "product";

/**
 * The `@context.guide` facet of {@link rumActionNames.reductionGuideDownloaded}. Closed here rather
 * than derived from `reduction-guides.ts` for the reason {@link NetworkKnowledgeItem} is.
 */
export type ReductionGuideId =
  | "scope-1-and-2"
  | "scope-3-general"
  | "scope-3-apparel"
  | "scope-3-food"
  | "scope-3-beverages"
  | "scope-3-packaging"
  | "scope-3-financial-services"
  | "scope-3-digital-services"
  | "scope-3-cosmetics"
  | "scope-3-industrial-goods"
  | "scope-3-hospitality";

/**
 * The `@context.item` facet of {@link rumActionNames.reductionKnowledgeOpened}. Closed here for the
 * reason {@link NetworkKnowledgeItem} is.
 */
export type ReductionKnowledgeItem =
  | "science-based-targets"
  | "decarbonisation-commercial-success"
  | "eac-procurement-strategy"
  | "ambition-to-action-recording";

/** Card type tracked alongside {@link ReductionKnowledgeItem} as the `@context.kind` facet. */
export type ReductionKnowledgeKind = "article" | "video";

/**
 * Tab selected on {@link rumActionNames.reductionPlansFiltered}. Not `ReductionPlanStatus`, whose
 * values are capitalised: a facet that mixes `Draft` with `draft` charts as two values.
 */
export type ReductionPlanFilter = "all" | "draft" | "submitted" | "active" | "closed";

/**
 * Solution tracked on {@link rumActionNames.supportRequested}. Declared apart from the homepage's
 * union so the vocabulary is closed independently of the UI: the request body carries a
 * human-readable solution for the support email, which must never become a facet value.
 */
export type RumSolution = "CCF" | "PCF" | "LEGACY" | "NETWORK" | "SOLUTION_SUBPAGE" | "REDUCTION";

/**
 * Surface a {@link rumActionNames.supportRequested} came from; `solution` alone cannot tell a plan
 * enquiry from a PCF volume enquiry.
 *
 * `network-pcf-declined-support` is the declined-PCF contact dialog. The other `network-*` values
 * are the support packages: they share one surface and one `NETWORK` solution, so origin is the
 * only facet that can carry which package was picked.
 */
export type SupportRequestOrigin =
  | "solution-card"
  | "pcf-volume"
  | "network-hotspot-analysis"
  | "network-supplier-enablement"
  | "network-supply-chain-decarbonisation"
  | "network-pcf-declined-support"
  | "solution-subpage-sbti"
  | "solution-subpage-empco"
  | "solution-subpage-scope-3"
  | "reduction-full-access";
